/**
 * Afrikaans: Contacts
 *
 * What this file covers: The contact book: contacts, fields, tags, segments, imports, consent and erasing a
 * person's data.
 *
 * Tone: Clear and careful. Consent and erasure wording is legal in effect: say exactly what happens to the
 * person's data.
 *
 * Afrikaans: Standard South African Afrikaans. Address the reader as "jy". Use the same Afrikaans word for the
 * same English term everywhere; menu.ts and common.ts set the words the other files follow. Afrikaans must stay
 * complete: the release checks fail if a key is missing.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const contacts: Catalog = {
  'contacts.title': 'Kontakte',
  'contacts.columns.locale': 'Taal',
  'contacts.search.label': 'Soek',
  'contacts.search.placeholder': 'Soek naam, nommer of e-pos',
  'contacts.shownOf': '{{count}} van {{total}}',
  'contacts.empty.hint': 'Voeg iemand met die hand by, of voer ŉ CSV van jou bestaande gehoor in.',
  'contacts.emptySearch': 'Niks pas by “{{query}}” nie',
  'contacts.emptySearch.hint': 'Dit deursoek die hele kontakboek, nie net die bladsy waarop jy is nie.',
  'contacts.filters.optIn': 'Inskrywingstatus',
  'contacts.filters.optIn.any': 'Enige toestemming',
  'contacts.filters.tag': 'Etiket',
  'contacts.filters.tag.any': 'Enige etiket',
  'contacts.form.msisdnHint': 'Tik dit soos jy dit sou skakel. Ons voeg die landkode by.',
  'contacts.form.localeHint': 'Die taal waarin sjablone gestuur word, en die een wat jou kletsbot met hierdie persoon gebruik wanneer hy dit praat.',
  'contacts.form.localeUnset': 'Nie gestel nie',
  'contacts.modal.delete.consent': 'Dit vee ook {{msisdn}} se toestemmingsgeskiedenis uit. Om op te hou boodskappe stuur maar die toestemmingsrekord te hou, merk hulle eerder as onttrek.',
  'contacts.toast.exported': 'Uitvoer afgelaai.',
  'contacts.toast.exportFailed': 'Uitvoer het misluk',
  'contacts.import.title': 'Voer kontakte in',
  'contacts.import.uploading': 'Laai {{file}} op…',
  'contacts.import.queued': 'In die tou, {{total}} rye.',
  'contacts.import.running': 'Voer in… {{done}} van {{total}}',
  'contacts.import.stillRunning': 'Voer nog in. Dit gaan in die agtergrond voort; herlaai die lys binnekort.',
  'contacts.import.doneTitle': 'Invoer voltooi',
  'contacts.import.done': '{{inserted}} bygevoeg, {{updated}} opgedateer.',
  'contacts.import.doneWithSkips': '{{inserted}} bygevoeg, {{updated}} opgedateer, {{skipped}} oorgeslaan omdat die nommer nie gelees kon word nie.',
  'contacts.import.failedTitle': 'Invoer het misluk',
  'contacts.import.format.title': 'CSV-formaat',
  'contacts.import.format.intro': 'Die eerste ry moet ’n opskrif wees wat elke kolom benoem. Dit is die kolomme wat die invoerder ken; enige ander opskrif word aan ’n pasgemaakte veld gekoppel.',
  'contacts.import.format.column': 'Kolom',
  'contacts.import.format.required': 'Verpligtend',
  'contacts.import.format.notes': 'Notas',
  'contacts.import.format.upsert': 'ŉ Ry waarvan die nommer reeds bestaan werk daardie kontak by eerder as om ŉ tweede te skep. Leë selle laat die bestaande waarde onaangeraak.',
  'contacts.import.format.template': 'Laai ŉ sjabloon af',
  'contacts.import.format.choose': 'Kies ŉ lêer',
  'contacts.import.csv.col.msisdn': 'Met die landkode (+27821234567) of soos jy dit skakel (0821234567). ŉ Nommer sonder kode neem die land wat jy voor invoer kies.',
  'contacts.import.csv.col.name': 'Word in die inboks gewys in plaas van die nommer.',
  'contacts.import.csv.col.email': 'Opsioneel. Word nie vir boodskappe gebruik nie.',
  'contacts.import.csv.col.locale': 'Tweeletterkode: en, af, zu, xh, st.',
  'contacts.import.csv.col.tags': 'Skei verskeie etikette met ŉ regopstreep, bv. vip|newsletter.',
  'contacts.optedIn': 'Ingestem',
  'contacts.optedOut': 'Onttrek',
  'contacts.description': 'Jou WhatsApp-kontakboek. Voer ’n CSV in of voeg kontakte een vir een by; kontakte wat onttrek het, word outomaties uit uitsendings uitgesluit.',
  'contacts.actions.importCsv': 'Voer CSV in',
  'contacts.loadError': 'Kon nie kontakte laai nie.',
  'contacts.shown': '{{count}} gewys',
  'contacts.empty': 'Nog geen kontakte nie: voer ’n CSV in of voeg een by.',
  'contacts.columns.msisdn': 'Telefoonnommer',
  'contacts.columns.optIn': 'Inskrywingstatus',
  'contacts.optIn.in': 'Ingeskryf',
  'contacts.optIn.out': 'Onttrek',
  'contacts.modal.delete.title': 'Skrap kontak?',
  'contacts.modal.delete.body': 'Skrap kontak {{msisdn}}? Dit kan nie ongedaan gemaak word nie.',
  'contacts.toast.importQueued': 'Invoer in tou geplaas (taak {{jobId}}).',
  'contacts.toast.importQueuedTitle': 'CSV-invoer',
  'contacts.toast.importFailed': 'Invoer het misluk',
  'contacts.toast.saved': 'Kontak {{msisdn}} gestoor.',
  'contacts.drawer.new': 'Nuwe kontak',
  'contacts.drawer.edit': 'Wysig kontak',
  'contacts.form.msisdnRequired': 'ŉ Telefoonnommer is verpligtend.',
  'contacts.form.tagsHint': 'Druk Enter na elke etiket. Etikette is hoe jy kontakte vir ’n uitsending groepeer.',
  'contacts.form.optIn': 'Ingeskryf',
  'contacts.form.optInHint': 'Af = uitgesluit van bemarkingsuitsendings.',
  'contacts.view.notFound': 'Kontak nie gevind nie.',
  'contacts.view.header.noOptIn': 'geen inskrywing',
  'contacts.view.header.created': 'geskep {{when}}',
  'contacts.view.backToAll': 'Alle kontakte',
  'contacts.view.tabs.profile': 'Profiel',
  'contacts.view.tabs.consent': 'Toestemming',
  'contacts.view.consent.hint': 'Elke aangetekende toestemming en onttrekking vir hierdie persoon, nuutste eerste. Dit is die rekord om in ŉ POPIA-versoek aan te haal.',
  'contacts.view.consent.empty': 'Geen toestemmingsgebeure aangeteken nie',
  'contacts.view.consent.empty.hint': 'Kontakte wat voor toestemmingsopsporing bygevoeg of in bulk ingevoer is, het geen gebeure nie. Die huidige instelling geld steeds.',
  'contacts.view.consent.error': 'Die toestemmingsgeskiedenis kon nie gelaai word nie.',
  'contacts.view.consent.entry': '{{channel}} · {{source}}',
  'contacts.view.consent.source.api': 'In die konsole of deur die API verander',
  'contacts.view.consent.source.stop_keyword': 'Het STOP op WhatsApp geantwoord',
  'contacts.view.consent.source.import': 'Deur ŉ CSV-invoer gestel',
  'contacts.view.consent.source.unknown': 'Bron nie aangeteken nie',
  'contacts.view.profile.channels': 'Kanale',
  'contacts.view.tabs.conversations': 'Gesprekke',
  'contacts.view.tabs.danger': 'Gevaar',
  'contacts.view.profile.locale': 'Lokaliteit',
  'contacts.view.profile.notOptedIn': 'nie ingeskryf nie',
  'contacts.view.profile.contactId': 'Kontak-ID',
  'contacts.view.tags.hint': 'Etikette dryf segmentering. Gebruik hulle om uitsendings af te baken en outomatisering te stuur.',
  'contacts.view.tags.empty': 'Nog geen etikette nie.',
  'contacts.view.tags.remove': 'Verwyder "{{tag}}"',
  'contacts.view.tags.addPlaceholder': 'bv. vip',
  'contacts.view.tags.toast.added': 'Etiket "{{tag}}" bygevoeg.',
  'contacts.view.tags.toast.removed': 'Etiket "{{tag}}" verwyder.',
  'contacts.view.tags.toast.updateFailed': 'Kon nie etikette bywerk nie.',
  'contacts.view.conversations.hint': 'Inkassie-drade vir {{msisdn}}. Open een om die geselsie voort te sit of die geskiedenis te sien.',
  'contacts.view.conversations.empty': 'Geen gesprekke met hierdie kontak nie.',
  'contacts.view.conversations.noPreview': '(geen voorskou)',
  'contacts.view.danger.optInHint': 'Bemarkingsuitsendings vereis ’n aktiewe inskrywing. Skakel af as die kontak gevra het om van bemarkingskanale verwyder te word.',
  'contacts.view.danger.markOptOut': 'Merk as onttrek',
  'contacts.view.danger.markOptIn': 'Merk as ingeskryf',
  'contacts.view.danger.deleteContact': 'Skrap kontak',
  'contacts.view.danger.deleteHint': 'Verwyder {{msisdn}} permanent uit jou kontaklys.',
  'contacts.view.danger.deleteButton': 'Skrap kontak…',
  'contacts.view.danger.modal.delete.title': 'Skrap {{msisdn}}?',
  'contacts.view.danger.modal.delete.body': 'Verwyder hierdie kontak en enige segmentlidmaatskappe permanent. Gesprekke en uitsendingsgeskiedenis met hierdie nommer bly behoue.',
  'contacts.view.danger.modal.delete.irreversible': 'Dit kan nie ongedaan gemaak word nie.',
  'contacts.view.danger.toast.markedOptOut': 'As onttrek gemerk.',
  'contacts.view.danger.toast.markedOptIn': 'As ingeskryf gemerk.',
  'contacts.view.danger.toast.optInFailed': 'Kon nie inskrywing bywerk nie.',
  'contacts.view.danger.toast.deleted': '{{msisdn}} geskrap.',
  'contacts.view.danger.toast.deleteFailed': 'Kon nie kontak skrap nie.',
  'contacts.actions.import': 'Voer in',
  'contacts.actions.create': 'Skep kontak',
  'contacts.export.csv': 'Voer uit as CSV',
  'contacts.export.xlsx': 'Voer uit as Excel (.xlsx)',
  'contacts.columns.contact': 'Kontak',
  'contacts.filters.clear': 'Maak filters skoon',
  'contacts.filters.tag.option': '{{tag}} ({{count}})',
  'contacts.select.all': 'Kies elke kontak op hierdie bladsy',
  'contacts.select.one': 'Kies {{name}}',
  'contacts.rowsPerPage': 'Rye per bladsy',
  'contacts.pagination': 'Bladsye',
  'contacts.pagination.previous': 'Vorige bladsy',
  'contacts.pagination.next': 'Volgende bladsy',
  'contacts.pagination.page': 'Bladsy {{page}}',
  'contacts.importCard.title': 'Voer kontakte in',
  'contacts.importCard.body': 'Laai ’n CSV- of Excel-lêer op om baie kontakte gelyk by te voeg of op te dateer. Rye pas op die foonnommer, dus werk ’n invoer die mense by wat jy reeds het.',
  'contacts.importCard.columns': 'Kolomme: msisdn, name, email, locale, tags, notes, plus een per pasgemaakte veld. Skei etikette met ’n kommapunt.',
  'contacts.importCard.template': 'Laai sjabloon af',
  'contacts.tags.add': 'Voeg ’n etiket by',
  'contacts.tags.remove': 'Verwyder die etiket {{tag}}',
  'contacts.form.tab.details': 'Besonderhede',
  'contacts.form.tab.notes': 'Notas',
  'contacts.form.fullName': 'Volle naam',
  'contacts.form.fullName.placeholder': 'bv. Mary Rodriguez',
  'contacts.form.phone': 'Foonnommer',
  'contacts.form.dialCode': 'Skakelkode',
  'contacts.form.notes': 'Notas',
  'contacts.form.notesHint': 'Enigiets wat die moeite werd is om van hierdie persoon te weet.',
  'contacts.form.notes.placeholder': 'Stel belang in die opleidingsprogram. Verkies ’n oproep weeksoggende.',
  'contacts.form.notes.private': 'Notas is intern. Hulle word nooit aan die kontak gestuur nie.',
  'contacts.form.save': 'Stoor kontak',
  'contacts.bulk.label': 'Aksies vir die gekose kontakte',
  'contacts.bulk.count': '{{count}} gekies',
  'contacts.bulk.tag': 'Etiket',
  'contacts.bulk.tagLabel': 'Etiket om toe te pas',
  'contacts.bulk.tagHint': 'Etikette is hoe jy kontakte groepeer. Stuur ’n uitsending aan almal met een.',
  'contacts.bulk.add': 'Voeg etiket by',
  'contacts.bulk.remove': 'Verwyder etiket',
  'contacts.bulk.tagged': '{{count}} kontakte as {{tag}} geëtiketteer.',
  'contacts.bulk.untagged': '{{tag}} van {{count}} kontakte verwyder.',
  'contacts.bulk.broadcast': 'Stuur aan ’n etiket',
  'contacts.bulk.clear': 'Maak keuse skoon',
  'contacts.showingOf': 'Wys {{count}} van {{total}} kontakte',
  'contacts.filters.title': 'Filters',
  'contacts.sortBy': 'Sorteer volgens {{column}}',
  'contacts.columns.optIn.hint': 'Of hierdie persoon steeds geboodskap mag word. Kontakte wat uitgeteken het, word uit elke uitsending gelaat.',
  'contacts.drawer.view': 'Kontak',
  'contacts.import.preview.title': 'Kontroleer die nommers voor invoer',
  'contacts.import.preview.reading': 'Lees {{file}}…',
  'contacts.import.preview.country': 'Land om te aanvaar',
  'contacts.import.preview.countryHint': 'Vir nommers wat sonder ŉ landkode geskryf is, soos 061 536 0809.',
  'contacts.import.preview.assumed': '{{country}} ({{dial}}) aanvaar vir {{count}} nommers.',
  'contacts.import.preview.international': '{{count}} nommers het reeds ŉ landkode gehad en is behou.',
  'contacts.import.preview.failed': '{{count}} nommers kon nie gelees word nie en sal oorgeslaan word.',
  'contacts.import.preview.allGood': 'Elke nommer kon gelees word.',
  'contacts.import.preview.row': 'Ry',
  'contacts.import.preview.input': 'Soos geskryf',
  'contacts.import.preview.reason': 'Hoekom',
  'contacts.import.preview.moreFailed': 'en nog {{count}}',
  'contacts.import.preview.sample': 'Byvoorbeeld, {{input}} word {{e164}}.',
  'contacts.import.preview.confirm': 'Voer {{count}} kontakte in',
  'contacts.import.preview.nothing': 'Niks om in te voer nie: geen nommer in die lêer kon gelees word nie.',
  'contacts.import.preview.failedToRead': 'Die lêer kon nie gelees word nie.',
  'contacts.actions.message': 'Stuur boodskap',
  'contacts.bulk.message': 'Stuur boodskap',
  'contacts.fields.title': 'Kontakvelde',
  'contacts.fields.description': 'Ekstra besonderhede wat jy oor elke kontak bewaar, buiten naam, nommer en e-pos. ’n Veld verskyn op die kontakvorm, in die lysfilter en in segmente, en reis saam met invoere en uitvoere.',
  'contacts.fields.new': 'Nuwe veld',
  'contacts.fields.edit': 'Wysig veld',
  'contacts.fields.empty': 'Nog geen pasgemaakte velde nie',
  'contacts.fields.emptyHint': 'Voeg ’n veld by vir enigiets wat die moeite werd is om oor ’n persoon te bewaar: ’n bestelnommer, ’n plan, ’n hernuwingsdatum.',
  'contacts.fields.loadError': 'Kon nie die kontakvelde laai nie.',
  'contacts.fields.columns.label': 'Etiket',
  'contacts.fields.columns.key': 'Sleutel',
  'contacts.fields.columns.type': 'Tipe',
  'contacts.fields.columns.usage': 'Gebruik',
  'contacts.fields.columns.active': 'Status',
  'contacts.fields.usage': '{{count}} kontakte',
  'contacts.fields.usage.one': '1 kontak',
  'contacts.fields.active': 'Aktief',
  'contacts.fields.inactive': 'Versteek',
  'contacts.fields.type.text': 'Teks',
  'contacts.fields.type.number': 'Getal',
  'contacts.fields.type.date': 'Datum',
  'contacts.fields.type.boolean': 'Ja / nee',
  'contacts.fields.type.select': 'Keuse',
  'contacts.fields.type.url': 'Webadres',
  'contacts.fields.form.label': 'Etiket',
  'contacts.fields.form.labelHint': 'Wat die veld op die kontakvorm genoem word.',
  'contacts.fields.form.key': 'Sleutel',
  'contacts.fields.form.keyHint': 'Volg die etiket totdat jy dit verander: kleinletters, syfers en onderstrepe. Dit benoem die kolom in invoere en uitvoere en die plekhouer in boodskappe.',
  'contacts.fields.form.keyLocked': 'Die sleutel kan nie verander sodra die veld bestaan nie: elke waarde wat op ’n kontak gestoor is, word daaronder geliasseer.',
  'contacts.fields.form.type': 'Tipe',
  'contacts.fields.form.typeHint': 'Hoe die waarde gestoor word, en hoe filters en segmente dit vergelyk.',
  'contacts.fields.form.typeLocked': 'Die tipe kan nie verander sodra die veld bestaan nie: waardes wat reeds gestoor is, sou ophou beteken wat hulle beteken het.',
  'contacts.fields.form.options': 'Opsies',
  'contacts.fields.form.optionsHint': 'Een per reël. ’n Kontak hou presies een hiervan.',
  'contacts.fields.form.optionsRequired': '’n Keuseveld het minstens een opsie nodig.',
  'contacts.fields.form.description': 'Beskrywing',
  'contacts.fields.form.descriptionHint': 'Word onder die veld op die kontakvorm gewys.',
  'contacts.fields.form.active': 'Aktief',
  'contacts.fields.form.activeHint': 'As jy ’n veld afskakel, word dit van die kontakvorm en die uitvoer versteek. Niks wat op enige kontak gestoor is, word geraak nie.',
  'contacts.fields.form.keyInvalid': 'Gebruik kleinletters, syfers en onderstrepe, en begin met ’n letter.',
  'contacts.fields.form.keyReserved': 'Elke kontak het reeds ’n veld met hierdie naam.',
  'contacts.fields.form.keyTaken': '’n Veld met hierdie sleutel bestaan reeds.',
  'contacts.fields.toast.created': 'Veld “{{label}}” bygevoeg',
  'contacts.fields.toast.updated': 'Veld “{{label}}” gestoor',
  'contacts.fields.toast.deleted': 'Veld “{{label}}” verwyder',
  'contacts.fields.toast.saveFailed': 'Kon nie die veld stoor nie',
  'contacts.fields.toast.deleteFailed': 'Kon nie die veld verwyder nie',
  'contacts.fields.delete.title': 'Verwyder “{{label}}”?',
  'contacts.fields.delete.body': '{{count}} kontakte dra ’n waarde vir hierdie veld. As jy dit verwyder, word daardie waardes van elkeen van hulle geskrap. Dit kan nie ongedaan gemaak word nie.',
  'contacts.fields.delete.bodyNone': 'Geen kontak dra nog ’n waarde vir hierdie veld nie.',
  'contacts.fields.delete.hint': 'Om die veld te versteek en die waardes te behou, skakel dit eerder af.',
  'contacts.fields.delete.confirm': 'Verwyder veld',
  'contacts.form.tab.fields': 'Velde',
  'contacts.form.fields.empty': 'Nog geen pasgemaakte velde nie',
  'contacts.form.fields.emptyHint': 'Definieer die ekstra besonderhede wat jy oor elke kontak bewaar en hulle verskyn hier.',
  'contacts.form.fields.manage': 'Stel kontakvelde op',
  'contacts.form.fields.manageHint': 'Kort ’n veld?',
  'contacts.form.fields.unset': 'Nie gestel nie',
  'contacts.form.fields.loadError': 'Kon nie die kontakvelde laai nie.',
  'contacts.form.fields.datePlaceholder': 'JJJJ-MM-DD',
  'contacts.form.fields.urlPlaceholder': 'https://',
  'contacts.form.fields.invalid.number': 'Voer ’n getal in.',
  'contacts.form.fields.invalid.date': 'Voer ’n datum in as JJJJ-MM-DD.',
  'contacts.form.fields.invalid.url': 'Voer ’n volledige webadres in wat met http:// of https:// begin.',
  'contacts.form.fields.invalid.select': 'Kies een van die opsies.',
  'contacts.form.fields.invalid.boolean': 'Kies ja of nee.',
  'contacts.form.fields.invalid.unknown': 'Hierdie veld bestaan nie meer nie. Herlaai en probeer weer.',
  'contacts.filters.field': 'Veld',
  'contacts.filters.field.any': 'Enige veld',
  'contacts.filters.field.value': 'Waarde',
  'contacts.filters.field.valueAny': 'Enige waarde',
  'contacts.filters.field.yes': 'Ja',
  'contacts.filters.field.no': 'Nee',
  'contacts.filters.field.placeholder': 'Tik ’n waarde',
  'contacts.import.preview.mapping': 'Kolomme',
  'contacts.import.preview.column': 'In die lêer',
  'contacts.import.preview.target': 'Gaan na',
  'contacts.import.preview.target.ignored': 'Geïgnoreer',
  'contacts.import.preview.target.unknown': 'Pas by niks',
  'contacts.import.preview.target.field': 'Veld: {{label}}',
  'contacts.import.preview.createFields': 'Skep die {{count}} onvergelykte kolomme as teksvelde',
  'contacts.import.preview.createFieldsHint': 'Elkeen word ’n pasgemaakte veld wat na sy opskrif genoem word, en sy waardes word saam met die res ingevoer. Laat dit af om daardie kolomme oor te slaan.',
  'contacts.import.preview.fieldProblems': '{{count}} waardes pas nie by hul veld se tipe nie. Hulle sal as teks bewaar word.',
  'contacts.import.preview.fieldProblem': 'Ry {{row}}, {{column}}: “{{value}}”',
  'contacts.import.doneFieldProblems': '{{count}} waardes het nie by hul veld se tipe gepas nie en is as teks bewaar.',
  'contacts.import.format.customFields': 'Enige ander kolom word aan ’n pasgemaakte veld gekoppel volgens sy sleutel of sy etiket. Kolomme wat by niks pas nie, kan uit die voorskou as teksvelde geskep word.',
  'contacts.import.csv.col.notes': 'Vryteksnotas wat teen die kontak bewaar word.',
  'contacts.remove.action': 'Verwyder',
  'contacts.remove.title': 'Verwyder {{msisdn}} uit jou kontakte?',
  'contacts.remove.body': 'Hul naam, e-pos, etikette, velde en notas word skoongemaak en hulle word as onttrek gemerk, sodat geen uitsending hulle per ongeluk bereik nie. Gesprekke met hulle bly.',
  'contacts.remove.back': 'As jy die nommer weer byvoeg, kom hulle terug, steeds onttrek. Om alles wat jy oor hulle hou uit te vee, gebruik eerder Vee hierdie persoon uit.',
  'contacts.remove.confirm': 'Verwyder',
  'contacts.remove.toast': '{{msisdn}} verwyder. Hulle bly onttrek.',
  'contacts.remove.toastTitle': 'Verwyder',
  'contacts.remove.failed': 'Kon nie die kontak verwyder nie.',
  'contacts.remove.cardTitle': 'Verwyder uit jou kontakte',
  'contacts.remove.cardHint': 'Haal {{msisdn}} uit jou lys en merk hulle as onttrek. Die nommer word gehou sodat hulle nooit per ongeluk ’n boodskap kry nie.',
  'contacts.remove.button': 'Verwyder kontak…',
  'contacts.erase.cardTitle': 'Vee hierdie persoon uit',
  'contacts.erase.cardHint': 'Vir ’n versoek om hul persoonlike inligting kragtens POPIA uit te vee. Vee die kontak, hul toestemmingsgeskiedenis, gesprekke, die lêers wat hulle gestuur het, vloeigeskiedenis, oproepopnames en transkripsies uit, en haal hul nommer van vorige uitsendings, boodskappe en rekeninglyne af.',
  'contacts.erase.button': 'Vee hierdie persoon uit…',
  'contacts.erase.modal.title': 'Vee {{msisdn}} uit',
  'contacts.erase.byNumber.title': 'Vee ’n telefoonnommer uit',
  'contacts.erase.byNumber.open': 'Vee ’n nommer uit…',
  'contacts.erase.modal.what': 'Alles wat jy oor hierdie persoon hou, word uitgevee: die kontak, hul toestemmingsgeskiedenis, gesprekke en die lêers wat hulle gestuur het, vloeigeskiedenis, oproepopnames en transkripsies. Hul nommer word van vorige uitsendings, boodskappe en rekeninglyne afgehaal, wat hul bedrae behou.',
  'contacts.erase.modal.keep': 'Net ’n eenrigtingkode wat van die nommer gemaak is, word gehou, sodat hulle onttrek bly as die nommer weer bygevoeg word. Dit kan nie ongedaan gemaak word nie.',
  'contacts.erase.modal.confirmLabel': 'Tik hul telefoonnommer om te bevestig',
  'contacts.erase.modal.confirmHint': 'Enige skryfwyse werk, soos 082 123 4567 of +27 82 123 4567.',
  'contacts.erase.modal.numberLabel': 'Telefoonnommer',
  'contacts.erase.modal.numberHint': 'Vir iemand wat nie in jou kontakte is nie: ’n persoon wat jy verwyder het, of wat jou net ’n boodskap gestuur het.',
  'contacts.erase.modal.reasonLabel': 'Rede (opsioneel)',
  'contacts.erase.modal.reasonHint': 'Vir jou eie rekord, byvoorbeeld "Versoek per e-pos op 2 Oktober". Laat hul naam en nommer uit.',
  'contacts.erase.modal.submit': 'Vee permanent uit',
  'contacts.erase.mismatch': 'Dit is nie hierdie kontak se nommer nie.',
  'contacts.erase.toast': 'Uitgevee. Alles wat oor die persoon gehou word, word uitgevee.',
  'contacts.erase.toastPending': 'Hier uitgevee. Die res word binne ’n minuut uitgevee.',
  'contacts.erase.toastTitle': 'Uitgevee',
  'contacts.erase.failed': 'Kon nie uitvee nie.',
  'contacts.form.smsOptIn': 'SMS-toestemming',
  'contacts.form.smsOptIn.unset': 'Nie aangeteken nie',
  'contacts.form.smsOptIn.yes': 'Het tot SMS ingestem',
  'contacts.form.smsOptIn.no': 'Het van SMS onttrek',
  'contacts.form.smsOptInHint': 'Bemarkings-SMS’e gaan net na mense wat tot SMS ingestem het. Herinneringe en kennisgewings gaan na almal wat nie onttrek het nie. ’n STOP-antwoord op ’n SMS onttrek die persoon. WhatsApp-toestemming tel nie vir SMS nie.',
  'contacts.sms.pill.yes': 'SMS: ingestem',
  'contacts.sms.pill.no': 'SMS: onttrek',
  'contacts.sms.pill.unset': 'SMS: nie aangeteken nie',
  'contacts.import.csv.col.sms_opt_in': 'SMS-toestemming: yes of no (true/false en 1/0 werk ook). ’n Leë sel laat dit soos dit is. WhatsApp-toestemming tel nie vir SMS nie.',
  'contacts.importCard.smsColumn': 'Voeg ’n sms_opt_in-kolom (yes of no) by om SMS-toestemming aan te teken.',
  'contacts.form.keyRequired': 'Voeg ’n foonnommer of ’n e-posadres by.',
  'contacts.form.emailInvalid': 'Dit is nie ’n e-posadres nie.',
  'contacts.form.emailOptIn': 'E-postoestemming',
  'contacts.form.emailOptIn.unset': 'Nie aangeteken nie',
  'contacts.form.emailOptIn.yes': 'Het tot e-pos ingestem',
  'contacts.form.emailOptIn.no': 'Het van e-pos onttrek',
  'contacts.form.emailOptInHint': 'Bemarkings-e-pos gaan net na mense wat tot e-pos ingestem het. Kennisgewings gaan na almal wat nie onttrek het nie. ’n Uitskryfskakel in ’n e-pos onttrek die persoon. WhatsApp-toestemming tel nie vir e-pos nie.',
  'contacts.email.pill.yes': 'E-pos: ingestem',
  'contacts.email.pill.no': 'E-pos: onttrek',
  'contacts.email.pill.unset': 'E-pos: nie aangeteken nie',
  'contacts.remove.cardHintEmail': 'Haal {{email}} uit jou lys.',
  'contacts.erase.modal.whatEmail': 'Alles wat jy oor hierdie persoon hou, word uitgevee: die kontak, hul toestemmingsgeskiedenis en hul e-posgesprekke.',
  'contacts.erase.modal.keepEmail': 'Net ’n eenrigtingkode wat van die adres gemaak is, word gehou, sodat hulle onttrek bly as die adres weer bygevoeg word. Dit kan nie ongedaan gemaak word nie.',
  'contacts.erase.modal.confirmEmailLabel': 'Tik hul e-posadres om te bevestig',
  'contacts.erase.modal.confirmEmailHint': 'Hoofletters maak nie saak nie.',
  'contacts.erase.mismatchEmail': 'Dit is nie hierdie kontak se e-posadres nie.',
  'contacts.erase.modal.numberOrEmailLabel': 'Telefoonnommer of e-posadres',
  'contacts.erase.byNumber.titleEither': 'Vee ’n telefoonnommer of e-posadres uit',
  'contacts.importCard.emailColumn': 'Geen foonnommers nie? ’n E-poskolom werk op sy eie, en ’n email_opt_in-kolom (yes of no) teken e-postoestemming aan.',
  'contacts.import.format.either': 'Foon of e-pos',
  'contacts.import.format.keyRule': '’n Lêer het ’n foonkolom (msisdn) of ’n e-poskolom nodig, of albei. ’n Ry sonder ’n nommer maar met ’n e-posadres word ’n kontak met net ’n e-posadres.',
  'contacts.import.csv.col.emailKey': '’n E-posadres. Nodig op ’n ry sonder ’n nommer.',
  'contacts.import.csv.col.email_opt_in': 'E-postoestemming: yes of no (true/false en 1/0 werk ook; die kolom mag ook email_consent heet). ’n Leë sel laat dit soos dit is. WhatsApp-toestemming tel nie vir e-pos nie.',
  'contacts.import.emailOnlyRows': '{{count}} kontakte met net ’n e-posadres.',
  'contacts.view.tabs.history': 'Geskiedenis',
  'contacts.changes.field.name': 'Naam',
  'contacts.changes.field.email': 'E-posadres',
  'contacts.changes.field.locale': 'Taal',
  'contacts.changes.noun.name': 'naam',
  'contacts.changes.noun.email': 'e-posadres',
  'contacts.changes.noun.locale': 'taal',
  'contacts.changes.emptyValue': '(leeg)',
  'contacts.changes.line.changed': '{{field}} is op {{date}} deur {{who}} van {{old}} na {{new}} verander.',
  'contacts.changes.line.cleared': '{{field}} is op {{date}} deur {{who}} leeggemaak; dit was {{old}}.',
  'contacts.changes.line.confirmed': 'Die kliënt het dit bevestig.',
  'contacts.changes.line.notAsked': 'Die kliënt is nie gevra nie.',
  'contacts.changes.line.kept': 'Die kliënt is gevra om hul {{noun}} na {{new}} te verander en het nee gesê; {{old}} het gebly.',
  'contacts.changes.banner.title': 'Veranderinge om na te gaan',
  'contacts.changes.banner.hint': 'Wat voorheen daar was, word gehou, so niks gaan verlore nie. Die Geskiedenis-oortjie wys elke verandering.',
  'contacts.changes.undo': 'Ontdoen',
  'contacts.changes.gotIt': 'Reg so',
  'contacts.changes.undone': '{{field}} is terug na {{value}}.',
  'contacts.changes.error.changedSince': 'Dit kan nie hier ontdoen word nie: dit is sedertdien weer verander, na {{current}}.',
  'contacts.changes.error.changedSinceEmpty': 'Dit kan nie hier ontdoen word nie: dit is sedertdien weer verander.',
  'contacts.changes.error.nothing': 'Daar is niks om te ontdoen nie: hierdie verandering het niks vervang nie.',
  'contacts.changes.error.notFound': 'Daardie verandering is nie meer op hierdie kontak nie.',
  'contacts.changes.error.role': 'Net die eienaar of ’n administrateur kan ’n verandering ontdoen.',
  'contacts.changes.error.load': 'Die geskiedenis kon nie gelaai word nie.',
  'contacts.changes.who.flow': '’n vloei',
  'contacts.changes.who.person': '’n spanlid',
  'contacts.changes.who.api': '’n integrasie',
  'contacts.changes.who.undo': 'Ontdoen deur {{person}}',
  'contacts.changes.history.hint': 'Elke verandering aan hierdie kontak se naam, e-posadres, taal en eie velde, nuutste eerste, met wat voorheen daar was.',
  'contacts.changes.history.empty': 'Nog geen veranderinge nie',
  'contacts.changes.history.empty.hint': 'Wanneer ’n vloei of iemand in jou span hierdie kontak se besonderhede verander, wys dit hier met wat voorheen daar was.',
  'contacts.changes.history.fromTo': '{{old}} na {{new}}',
  'contacts.changes.history.kept': '{{old}} het gebly; {{new}} is van die hand gewys',
  'contacts.changes.history.by': 'Deur {{who}}',
  'contacts.changes.badge.confirmed': 'Kliënt het bevestig',
  'contacts.changes.badge.declined': 'Kliënt het nee gesê',
  'contacts.changes.badge.unreviewed': 'Nog nie nagegaan nie',
  'contacts.drawer.openPage': 'Maak volle bladsy oop',
  'contacts.page.description': 'Alles oor hierdie persoon: hul besonderhede, gesprekke, toestemming en wat verander het.',
  'contacts.page.inList': 'Wys in die lys',
  'contacts.filters.language': 'Taal',
  'contacts.filters.language.any': 'Enige taal'
};

export default contacts;
